// **Benutzer hinzufügen** — eine Seite, kein Dialog (`?add=1`). Angelegt wird
// in der Instanz: mit lokalen Konten als Einladung zum Passwortsetzen, mit
// Microsoft/OIDC als freigeschaltetes Konto, das die erste Anmeldung mit der
// E-Mail übernimmt.

import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ApiError, post } from "../../api/client";
import { keys } from "../../api/queries";
import type { AddUserResponse, InstanceUsersResponse, Tenant } from "../../api/types";
import { errorText } from "../../lib/labels";
import { Link, useNavigate } from "../../router/router";
import { Note } from "../../ui/badge";
import { Card } from "../../ui/card";
import { CopyField } from "../../ui/copy-field";
import { Check, Field, Input } from "../../ui/field";
import { SelectMenu } from "../../ui/select-menu";
import { toast } from "../../ui/toaster";
import { USER_ERRORS, addUserBody, defaultRole, preferredSeat, seatsOn as seatsOnOf, type AddUserState } from "./users-helpers";

export function AddUser({ tenant: t, data, here }: { tenant: Tenant; data: InstanceUsersResponse; here: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const roles = data.roles ?? [];
  const local = data.sign_in === "local";
  const seatsOn = seatsOnOf(data);
  const initial = (): AddUserState => ({ email: "", name: "", roleId: defaultRole(data), licenseKey: preferredSeat(data), admin: false });
  const [s, setS] = useState<AddUserState>(initial);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const set = (patch: Partial<AddUserState>) => setS((cur) => ({ ...cur, ...patch }));

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      const res = await post<AddUserResponse>(`/tenants/${t.id}/users`, addUserBody(s, { hasRoles: roles.length > 0, seatsOn }));
      void queryClient.invalidateQueries({ queryKey: keys.tenantUsers(t.id) });
      if (res.invite_url) { setInviteUrl(res.invite_url); return; }
      toast(res.sign_in === "local" ? "Einladung verschickt" : "Benutzer hinzugefügt");
      if (res.license_warning) toast(res.license_warning, "error");
      navigate(here, { replace: true });
    } catch (err) {
      setError((err instanceof ApiError ? USER_ERRORS[err.code] : undefined) ?? errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (inviteUrl) {
    // Der Link steht nirgends sonst — hier einmal, zum Weitergeben.
    return (
      <Card
        title="Benutzer hinzugefügt"
        foot={<>
          <Link className="pf-btn" to={`${here}?add=1`} onClick={() => { setInviteUrl(null); setS(initial()); }}>Noch einen hinzufügen</Link>
          <Link className="pf-btn pf-btn--primary" to={here}>Zur Liste</Link>
        </>}
      >
        <Note tone="warn">
          Es ging keine E-Mail hinaus. Diesen Link an die Person weitergeben (7 Tage gültig) — er wird nur <strong>dieses eine Mal</strong> angezeigt:
          <CopyField value={inviteUrl} onCopied={() => toast("Kopiert")} />
        </Note>
      </Card>
    );
  }

  return (
    <Card
      title="Benutzer hinzufügen"
      hint={local
        ? "Die Person bekommt eine E-Mail mit einem Link, um ihr Passwort zu setzen. Ohne Mailversand zeigt die Plattform den Link einmal an."
        : <>Das Konto ist sofort freigeschaltet. Die Person meldet sich unter <a href={`https://${t.host}`} target="_blank" rel="noopener">{t.host}</a> mit dieser E-Mail an — die erste Anmeldung übernimmt das Konto.</>}
      foot={<>
        <Link className="pf-btn" to={here}>Abbrechen</Link>
        <button type="button" className="pf-btn pf-btn--primary" disabled={busy} onClick={() => void submit()}>{local ? "Einladen" : "Hinzufügen"}</button>
      </>}
    >
      <form className="pf-list" noValidate onSubmit={(e) => void submit(e)}>
        <div className="pf-grid">
          <Field label="E-Mail">
            <Input name="email" type="email" autoComplete="off" spellCheck={false} required autoFocus value={s.email} onChange={(e) => set({ email: e.target.value })} />
          </Field>
          <Field label="Name (optional)"><Input name="name" value={s.name} onChange={(e) => set({ name: e.target.value })} /></Field>
          {roles.length > 0 && (
            <Field label="Rolle">
              <SelectMenu
                name="role_id" ariaLabel="Rolle" value={s.roleId} disabled={s.admin} onChange={(v) => set({ roleId: v })}
                options={roles.map((r) => [String(r.id), r.is_default ? `${r.name} (Vorgabe)` : r.name] as const)}
              />
            </Field>
          )}
          {seatsOn && (
            <Field label="Lizenz">
              <SelectMenu
                name="license_key" ariaLabel="Lizenz" value={s.licenseKey} onChange={(v) => set({ licenseKey: v })}
                options={[["", "keine Lizenz"], ...data.seats.map((x) => [x.key, `${x.name} (${Math.max(0, x.count - x.used)} frei)`] as const)]}
              />
            </Field>
          )}
        </div>
        <Check
          name="admin" checked={s.admin} onChange={(admin) => set({ admin })} label="Administrator"
          hint="verwaltet Benutzer, Rollen, Arbeitsbereiche und sieht das Audit-Log; die Rolle zählt dann nicht"
        />
        <span className="pf-field__error">{error}</span>
      </form>
    </Card>
  );
}
